//! @see: https://ui.shadcn.com/docs/components/combobox
import { Check, ChevronsUpDown, SearchIcon } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Primitive, isNil, isPrimitive } from "helper/utils";

import { Button } from "@/components/ui/button";
import { LoadingIcon } from "assets";
import { ScrollInfinite } from "@/components/ScrollInfinite";
import { cn } from "@/lib/utils";

export type OptionType<T extends Primitive = string> = {
  value: T;
  label: string;
  description?: string;
};

export type DropdownAutoCompleteSelectProps<T extends Primitive = string> = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick"
> & {
  value: T | undefined | null;
  onChangeValue?: (value: string, label: string) => void;
  options: T extends Primitive ? T[] | OptionType<T>[] : OptionType<T>[];
  className?: string;
  placeholder?: string;
  handleSearch?: (value: string) => void;
  searchString?: string;
  isLoading?: boolean;
  panelClassName?: string;
  inputClassName?: string;
  displayValue?: T;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onLoadMore?: () => void;
  hasMore?: boolean;
};

const emptyOption: OptionType[] = [];

export const DropdownAutoCompleteSelect = forwardRef<HTMLDivElement, DropdownAutoCompleteSelectProps>(
  function DropdownSelect(
    {
      placeholder = "Search address...",
      onChangeValue,
      value = "",
      options = emptyOption,
      className,
      handleSearch,
      searchString = "",
      panelClassName,
      inputClassName,
      isLoading,
      onClick,
      displayValue,
      onLoadMore,
      hasMore,
      ...selectProps
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<OptionType | null>(null);
    const { disabled } = selectProps;
    const displayValueRef = useRef<HTMLButtonElement>(null);

    const optionData = useMemo(
      () =>
        options.map((opt) =>
          isPrimitive(opt)
            ? {
                value: opt,
                label: String(opt),
              }
            : opt,
        ),
      [options],
    );

    useEffect(() => {
      if (isNil(value)) {
        setSelectedItem(null);
      } else {
        const newSelected = optionData.find((opt) => opt.value === value);
        if (newSelected) {
          setSelectedItem(newSelected);
        }
      }
    }, [value, optionData]);

    const displayText = useMemo(() => {
      if (displayValue) {
        return displayValue;
      }
      return selectedItem ? selectedItem.label : placeholder;
    }, [selectedItem, displayValue, placeholder]);

    const renderOptions = () =>
      optionData.map((item, itemIdx) => {
        const selected = item.value === value;
        return (
          <CommandItem
            key={`${item.value}-${itemIdx}`}
            value={item.value}
            onSelect={() => {
              if (onChangeValue) {
                onChangeValue(item.value, item.label);
              }
              setOpen(false);
            }}
            className="cursor-pointer"
          >
            <div>
              {item.label}
              {item?.description && <span className="text-neutral-600">{item.description}</span>}
            </div>
            <Check className={cn("ml-auto", selected ? "opacity-100" : "opacity-0")} />
          </CommandItem>
        );
      });

    return (
      <div ref={ref}>
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              className={cn("w-full justify-between", className, disabled && "text-disabled")}
              variant="outline"
              ref={displayValueRef}
              onClick={onClick}
            >
              <span className="block truncate pr-3">{displayText}</span>
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn("p-0", panelClassName)} style={{ width: displayValueRef.current?.offsetWidth }}>
            <Command>
              {handleSearch && (
                <div data-slot="command-input-wrapper" className="flex h-9 items-center gap-2 border-b px-3">
                  <SearchIcon className="size-4 shrink-0 opacity-50" />
                  <input
                    data-slot="command-input"
                    className={cn(
                      "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                      inputClassName,
                    )}
                    placeholder={placeholder ?? "Search"}
                    value={searchString}
                    onChange={(ev) => handleSearch(ev.target.value)}
                  />
                </div>
              )}
              <CommandList>
                {isLoading && !onLoadMore && (
                  <div className="text-center p-3">
                    <LoadingIcon className="w-5 h-5 inline-block" />
                  </div>
                )}
                <CommandEmpty>No record found.</CommandEmpty>
                <CommandGroup>
                  {onLoadMore ? (
                    <ScrollInfinite
                      onLoadMore={onLoadMore}
                      hasNextPage={hasMore}
                      isLoading={isLoading}
                      allRows={optionData}
                    >
                      {renderOptions()}
                    </ScrollInfinite>
                  ) : (
                    renderOptions()
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
